// Масштаб и размеры Ганта. Ширина дня — по шагам «Крупно | Средне | Обзор»,
// остальное — по устройству: на телефоне крупнее шрифт, полоска и строки,
// на компьютере размеры прежние. Подпись полоски — сколько влезает.

export type GanttZoom = 'large' | 'medium' | 'overview'

export const ZOOMS: readonly GanttZoom[] = ['large', 'medium', 'overview']

export const ZOOM_TITLE: Record<GanttZoom, string> = { large: 'Крупно', medium: 'Средне', overview: 'Обзор' }

/** Ширина дня по шагам: на компьютере «Средне» — прежние 32 px. */
const DAY_W: Record<'desktop' | 'phone', Record<GanttZoom, number>> = {
  desktop: { large: 48, medium: 32, overview: 20 },
  phone: { large: 48, medium: 36, overview: 24 },
}

export interface GanttDims {
  dayW: number
  /** Колонка подписей слева. */
  labelW: number
  /** Плановая полоска. */
  planH: number
  /** Фактическая полоска под ней. */
  factH: number
  /** Дорожка: полоска, зазор, факт и зазор до следующей. */
  laneH: number
  /** Отступ дорожек от верха строки (и снизу). */
  pad: number
  /** Строка без полосок: подпись в две строки. */
  rowMin: number
  /** Шрифт подписи на полоске, px. */
  barFont: number
  /** Поле подписи полоски слева и справа. */
  barPad: number
}

/** Без запомненного: на телефоне «Крупно», на компьютере прежний размер. */
export function defaultZoom(desktop: boolean): GanttZoom {
  return desktop ? 'medium' : 'large'
}

export function ganttDims(zoom: GanttZoom, desktop: boolean): GanttDims {
  if (desktop) {
    return { dayW: DAY_W.desktop[zoom], labelW: 240, planH: 20, factH: 4, laneH: 32, pad: 3, rowMin: 36, barFont: 11, barPad: 6 }
  }
  return { dayW: DAY_W.phone[zoom], labelW: 136, planH: 28, factH: 4, laneH: 40, pad: 4, rowMin: 48, barFont: 13, barPad: 8 }
}

/** Шаг крупнее (`+1`) или мельче (`-1`); на краю — тот же. */
export function stepZoom(zoom: GanttZoom, dir: 1 | -1): GanttZoom {
  const i = ZOOMS.indexOf(zoom) - dir
  return ZOOMS[Math.max(0, Math.min(ZOOMS.length - 1, i))] ?? zoom
}

export interface BarLabel {
  text: string
  /** Имя исполнителя уже в тексте. */
  withName: boolean
  /** Влез аватар исполнителя после текста. */
  avatar: boolean
}

export interface BarLabelInput {
  /** Ширина под подпись, без полей, px. */
  inner: number
  /** `short` этапа или заголовок карточки. */
  label: string
  /** Имя исполнителя; null — не писать. */
  name: string | null
  /** Ширина аватара с зазором; null — аватар не ставить. */
  avatarW: number | null
  measure: (text: string) => number
}

/**
 * Подпись полоски по ширине. Сначала «Монтаж · Аня», если влезает целиком;
 * иначе подпись и аватар, если влез аватар; иначе одна подпись, её обрежет
 * многоточие. Если не влезают и две буквы с многоточием — пусто: огрызок
 * в одну букву читается хуже, чем цвет полоски.
 */
export function barLabel({ inner, label, name, avatarW, measure }: BarLabelInput): BarLabel {
  const empty = { text: '', withName: false, avatar: false }
  if (!label || inner <= 0) return empty
  if (name) {
    const full = `${label} · ${name}`
    if (measure(full) <= inner) return { text: full, withName: true, avatar: false }
  }
  const width = measure(label)
  if (avatarW !== null && width + avatarW <= inner) return { text: label, withName: false, avatar: true }
  if (width <= inner) return { text: label, withName: false, avatar: false }
  if (measure(`${label.slice(0, 2)}…`) > inner) return empty
  return { text: label, withName: false, avatar: false }
}

/** Имя на полоске — первое слово: «Анна Петрова» → «Анна». */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? ''
}
